import { Circle, MessageSquare } from 'lucide-react';
import { T, useT } from '@transifex/react';
import type { components } from '~/api/openapi';
import { LiveTimestamp } from './live-timestamp';
import { getReactorName, getUniqueReactorsAndNames } from './reactions/utils';
import { StarIcon } from '~/components/ui/gamification/icons/star-rating';

type CommentNotification = components['schemas']['CommentsReceivedDto'];
type SubmissionReaction = components['schemas']['SubmissionEmojiReactionsReceivedDto'];
type CommentReaction = components['schemas']['CommentEmojiReactionsReceivedDto'];
type SectionType = components['schemas']['CommentsReceivedDto']['sectionType'];

export type StarRatingNotification = {
  starsGiven: number;
  sectionTitle: string;
  sectionType: SectionType;
  journeyName: string;
  ratedAt: string;
  isSeen: boolean;
};

const sectionConfig: Partial<Record<SectionType, { bg: string; icon: string }>> = {
  Quiz: { bg: 'bg-primary-50', icon: '/assets/notifications/quiz.svg' },
  StickyNote: { bg: 'bg-yellow-50', icon: '/assets/notifications/sticky.svg' },
  ReviewAndComment: { bg: 'bg-blue-50', icon: '/assets/notifications/review-comment.svg' },
  UploadContent: { bg: 'bg-green-50', icon: '/assets/notifications/upload.svg' },
};

export function NotificationActivityIcon({
  sectionType,
  contextType,
  emoji,
}: {
  sectionType: SectionType;
  contextType: 'comment' | 'reaction' | 'star';
  emoji?: string;
}) {
  return (
    <div className="relative size-[60px] shrink-0">
      <div
        className={`
          ${sectionConfig[sectionType]?.bg ?? 'bg-primary-50'} flex size-[60px] items-center justify-center rounded-sm`}
      >
        <img
          src={sectionConfig[sectionType]?.icon ?? '/assets/notifications/quiz.svg'}
          alt={sectionType}
          className="size-[22px]"
          loading="lazy"
        />
      </div>
      <div
        className="
          absolute top-10 left-10 flex size-6 items-center justify-center
          rounded-full bg-background-0 shadow-sm
        "
      >
        {contextType === 'star' ? (
          <StarIcon state="filled" size="xs" />
        ) : contextType === 'comment' ? (
          <MessageSquare className="size-3.5 text-text-500" />
        ) : (
          <span className="text-sm">{emoji}</span>
        )}
      </div>
    </div>
  );
}

function NotificationTimestamp({
  date,
  isSeen,
  className,
}: {
  date: string;
  isSeen: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <LiveTimestamp date={date} className="text-body-small text-text-500" updateInterval={30000} />
      {!isSeen && <Circle className="size-2 fill-blue-500 stroke-blue-500" />}
    </div>
  );
}

export function CommentNotificationItem({ comment }: { comment: CommentNotification }) {
  const displayName = getReactorName(comment.commenter);

  return (
    <div
      className="
        flex flex-wrap items-center gap-x-lg gap-y-xs px-lg py-md
        md:flex-nowrap md:items-start
      "
    >
      <NotificationActivityIcon sectionType={comment.sectionType} contextType="comment" />
      <NotificationTimestamp
        date={comment.commentedAt}
        isSeen={comment.isSeen}
        className="
          ml-auto flex shrink-0 items-center gap-sm
          md:hidden
        "
      />
      <div
        className="
          flex min-w-0 basis-full flex-col gap-xs
          md:flex-1 md:basis-auto
        "
      >
        <div className="flex items-center gap-xs">
          <p
            className="
              min-w-0 text-text-900
              md:truncate
            "
          >
            <span className="font-semibold">{displayName}</span>{' '}
            <T _str="commented on {sectionTitle}" sectionTitle={comment.sectionTitle} />
          </p>
          <NotificationTimestamp
            date={comment.commentedAt}
            isSeen={comment.isSeen}
            className="
              ml-auto hidden shrink-0 items-center gap-sm
              md:flex
            "
          />
        </div>
        <p className="line-clamp-2 text-text-700">{comment.comment}</p>
        <p className="text-text-500">{comment.journeyName}</p>
      </div>
    </div>
  );
}

export function SubmissionReactionNotificationItem({ reaction }: { reaction: SubmissionReaction }) {
  const { submissionContext, submissionEmojiReactions } = reaction;
  const { reactionLogs, emojiReactions, lastReactedAt, receiverSeen } = submissionEmojiReactions;

  const { allReactors } = getUniqueReactorsAndNames(reactionLogs);
  const hasMultipleReactors = allReactors.length > 1;
  const person = reactionLogs[0]?.reactedBy;
  const displayName = getReactorName(person);

  return (
    <div
      className="
        flex flex-wrap items-center gap-x-lg gap-y-xs px-lg py-md
        md:flex-nowrap md:items-start
      "
    >
      <NotificationActivityIcon
        sectionType={submissionContext.sectionType}
        contextType="reaction"
        emoji={reactionLogs[0]?.emoji}
      />
      <NotificationTimestamp
        date={lastReactedAt}
        isSeen={receiverSeen}
        className="
          ml-auto flex shrink-0 items-center gap-sm
          md:hidden
        "
      />
      <div
        className="
          flex min-w-0 basis-full flex-col gap-xs
          md:flex-1 md:basis-auto
        "
      >
        <div className="flex items-center gap-xs">
          <p
            className="
              min-w-0 text-text-900
              md:truncate
            "
          >
            <span className="font-semibold">{displayName}</span>{' '}
            {hasMultipleReactors ? (
              <T
                _str="and others reacted to your submission {sectionTitle}"
                sectionTitle={submissionContext.sectionTitle}
              />
            ) : (
              <T
                _str="reacted to your submission {sectionTitle}"
                sectionTitle={submissionContext.sectionTitle}
              />
            )}
          </p>
          <NotificationTimestamp
            date={lastReactedAt}
            isSeen={receiverSeen}
            className="
              ml-auto hidden shrink-0 items-center gap-sm
              md:flex
            "
          />
        </div>
        {submissionContext.journeyName && (
          <p className="text-text-500">{submissionContext.journeyName}</p>
        )}
        <div className="flex flex-wrap items-center gap-xs">
          {emojiReactions.map(r => (
            <div
              key={r.emoji}
              className="
                flex h-8 items-center gap-xs rounded-md bg-background-100 p-1
              "
            >
              <span className="text-xl">{r.emoji}</span>
              <span className="text-body-caption text-text-700">{r.reactedBy.length}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StarRatingNotificationItem(props: StarRatingNotification) {
  const { starsGiven, sectionTitle, sectionType, journeyName, ratedAt, isSeen } = props;
  const t = useT();

  return (
    <div
      className="
        flex flex-wrap items-center gap-x-lg gap-y-xs px-lg py-md
        md:flex-nowrap md:items-start
      "
    >
      <NotificationActivityIcon sectionType={sectionType} contextType="star" />
      <NotificationTimestamp
        date={ratedAt}
        isSeen={isSeen}
        className="
          ml-auto flex shrink-0 items-center gap-sm
          md:hidden
        "
      />
      <div
        className="
          flex min-w-0 basis-full flex-col gap-xs
          md:flex-1 md:basis-auto
        "
      >
        <p
          className="
            min-w-0 text-text-900
            md:truncate
          "
        >
          <T
            _str="You received {stars} for {sectionTitle}"
            stars={
              <span className="font-semibold">
                {t('{count, plural, one {# star} other {# stars}}', { count: starsGiven })}
              </span>
            }
            sectionTitle={sectionTitle}
          />
        </p>
        <p className="text-text-500">{journeyName}</p>
      </div>
      <NotificationTimestamp
        date={ratedAt}
        isSeen={isSeen}
        className="
          ml-auto hidden shrink-0 items-center gap-sm
          md:flex
        "
      />
    </div>
  );
}

export function CommentReactionNotificationItem({ reaction }: { reaction: CommentReaction }) {
  const { submissionContext, commentEmojiReactions, comment } = reaction;
  const { reactionLogs, emojiReactions, lastReactedAt, receiverSeen } = commentEmojiReactions;

  const { allReactors } = getUniqueReactorsAndNames(reactionLogs);
  const hasMultipleReactors = allReactors.length > 1;
  const person = reactionLogs[0]?.reactedBy;
  const displayName = getReactorName(person);

  return (
    <div
      className="
        flex flex-wrap items-center gap-x-lg gap-y-xs px-lg py-md
        md:flex-nowrap md:items-start
      "
    >
      <NotificationActivityIcon
        sectionType={submissionContext.sectionType}
        contextType="reaction"
        emoji={reactionLogs[0]?.emoji}
      />
      <NotificationTimestamp
        date={lastReactedAt}
        isSeen={receiverSeen}
        className="
          ml-auto flex shrink-0 items-center gap-sm
          md:hidden
        "
      />
      <div
        className="
          flex min-w-0 basis-full flex-col gap-xs
          md:flex-1 md:basis-auto
        "
      >
        <div className="flex items-center gap-xs">
          <p
            className="
              min-w-0 text-text-900
              md:truncate
            "
          >
            <span className="font-semibold">{displayName}</span>{' '}
            {hasMultipleReactors ? (
              <T _str="and others reacted to your comment" />
            ) : (
              <T _str="reacted to your comment" />
            )}
          </p>
          <NotificationTimestamp
            date={lastReactedAt}
            isSeen={receiverSeen}
            className="
              ml-auto hidden shrink-0 items-center gap-sm
              md:flex
            "
          />
        </div>
        <p className="line-clamp-2 text-text-700">{comment?.comment}</p>
        <div className="flex flex-wrap items-center gap-xs">
          {emojiReactions.map(r => (
            <div
              key={r.emoji}
              className="
                flex h-8 items-center gap-xs rounded-md bg-background-100 p-1
              "
            >
              <span className="text-xl">{r.emoji}</span>
              <span className="text-body-caption text-text-700">{r.reactedBy.length}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
